import {DatePickerInput as PeacockDatePickerInput} from '@pepsico-ds/ui'
import {useDatePickerValidation} from './UseDatePicker'
import {DATE_VALIDATION_MESSAGE} from '@/utils/constants/commonConstants'
import {getTimezoneByCountryCode} from '@/utils/constants/timeZones'
import {DateTime} from 'luxon'

/**
 * @typedef {import('@pepsico-ds/ui').DatePickerInputProps} DatePickerProps
 * @property {string | Date | null | undefined} [value] Optional legacy value used when selected is not provided.
 * @property {(value: string | undefined) => void} [onChange] Optional legacy callback used when setSelected is not provided.
 * @property {Array<{type: string, msg?: string, endDate?: string, startDate?: string, schedule?: string}>} [validationRules] Optional date validation rules.
 * @property {string} [dependentDate] Optional dependent date used to trigger re-validation when related date changes.
 * @property {string} [disabledHelperText] Optional helper text shown when disabled.
 */

/**
 * Wrapper around Peacock DS Date.
 * Supports Peacock DatePickerInput props and preserves value/onChange compatibility.
 * @param {DatePickerProps} props
 */
export const DatePicker = props => {
	const {
		label,
		selected,
		setSelected,
		value,
		onChange,
		validationRules = [],
		dependentDate,
		disabled = false,
		disabledHelperText = DATE_VALIDATION_MESSAGE.END_DATE_HELPER_TEXT,
		helperText,
		error,
		format = 'mm/dd/yyyy',
		placeholder = 'mm/dd/yyyy',
		endYear = new Date().getFullYear() + 5,
		...rest
	} = props

	const resolvedSelected = selected ?? value
	const {validateDates} = useDatePickerValidation(label, validationRules)

	const validationMessage = resolvedSelected ? validateDates(resolvedSelected) || '' : ''

	const normalizeSelectedDate = rawDate => {
		if (!rawDate) return rawDate

		const timezone = getTimezoneByCountryCode()
		if (!timezone) return rawDate

		let parsedDateTime

		if (rawDate instanceof Date) {
			parsedDateTime = DateTime.fromJSDate(rawDate, {zone: timezone})
		} else if (typeof rawDate === 'string') {
			const trimmedDate = rawDate.trim()
			if (!trimmedDate) return ''

			if (trimmedDate.includes('T')) {
				parsedDateTime = DateTime.fromISO(trimmedDate, {zone: timezone})
			}

			if (!parsedDateTime?.isValid) {
				parsedDateTime = DateTime.fromFormat(trimmedDate, 'MM/dd/yyyy', {zone: timezone})
			}

			if (!parsedDateTime?.isValid) {
				parsedDateTime = DateTime.fromFormat(trimmedDate, 'yyyy-MM-dd', {zone: timezone})
			}
		}

		if (!parsedDateTime?.isValid) return rawDate

		return parsedDateTime.startOf('day').toISO({suppressMilliseconds: true})
	}

	const handleSetSelected = rawDate => {
		const normalizedDate = normalizeSelectedDate(rawDate)

		if (typeof setSelected === 'function') {
			setSelected(normalizedDate)
			return
		}

		if (typeof onChange === 'function') {
			onChange(normalizedDate)
		}
	}

	const resolvedSetSelected =
		handleSetSelected

		// Force re-render dependency in cases where only related date changes in parent.
		void dependentDate

	return (
		<PeacockDatePickerInput
			selected={resolvedSelected}
			setSelected={resolvedSetSelected}
			format={format}
			placeholder={placeholder}
			endYear={endYear}
            error={error || Boolean(validationMessage)}
            helperText={disabled ? disabledHelperText : validationMessage || helperText}
            disabled={disabled}
			{...rest}
		/>
	)
}
